/**
 * Collection filter
 *
 * The collection filter class received url query parameters used to filter api results.
 *
 * Based on the following API Query Parser:
 * https://github.com/ngabor84/lumen-api-query-parser
 *
 */
export default class CollectionFilter {

  /**
   *
   * @param limit
   * @param page
   */
  constructor(limit, page) {

    // init data containers
    this.resetConnections();
    this.resetSort();
    this.resetDefaultSort();
    this.resetFilters();
    this.resetStaticFilters();

    // init limit and page
    this.setPage(page);
    this.setLimit(limit);
  }

  /**
   * caches the current filter set as the static filter set.
   * note: if a static filter set already exists the existing set will remain.
   * @returns {CollectionFilter}
   */
  cacheStaticFilters() {

    if (!Array.isArray(this.static_filters) || !this.static_filters.length) {
      // set the static filter set as the current filter set
      this.static_filters = this.filters.slice();

      // reset the current filter set
      this.resetFilters();
    }
    return this;
  }

  /**
   *
   * @returns {CollectionFilter}
   */
  resetConnections() {
    this.connections = [];
    return this;
  }

  /**
   * Reset the query filters array to an empty array
   * @returns {CollectionFilter}
   */
  resetFilters() {
    this.filters = [];
    return this;
  }

  /**
   * resets the static filters array back to an empty array
   * @returns {CollectionFilter}
   */
  resetStaticFilters() {
    this.static_filters = [];
    return this;
  }

  /**
   *
   * @returns {CollectionFilter}
   */
  resetSort() {
    this.sort = [];
    return this;
  }

  resetDefaultSort() {
    this.default_sort = [];
    return this;
  }

  /**
   * returns query string
   */
  getQuery() {
    return this.getQueryComponents().join('&');
  }

  /**
   * return query components array
   * @returns {Array}
   */
  getQueryComponents() {

    // int component array
    let components = [];

    // get filters
    let filters = this.mergeFilters();
    let sort = this.mergeSort();

    // format connections
    if (this.connections.length) {
      this.connections.forEach(function (value) {
        components.push('connection[]=' + value.connection);
      });
    }

    // format filters
    if (filters.length) {
      filters.forEach(function (value) {
        components.push(
          'filter[]=' + [
            value.field,
            value.operator,
            value.value,
            value.logic
          ].join(':')
        );
      });
    }

    // format sort order
    if (sort.length) {
      sort.forEach(function (value) {
        components.push(
          'sort[]=' + [
            value.field,
            value.order
          ].join(':')
        );
      });
    }

    // format limit
    if (this.getLimit()) {
      components.push('limit=' + this.getLimit());
    }

    // format page / page
    if (this.getPage()) {
      components.push('page=' + this.getPage());
    }

    // search
    if (this.search) {
      components.push('search=' + this.search)
    }

    // return url components
    return components;
  }

  /**
   * set limit
   * @param limit
   * @returns {CollectionFilter}
   */
  setLimit(limit) {
    // default
    this.limit = parseInt(limit);
    return this;
  }

  /**
   * @returns {number}
   */
  getLimit() {
    return parseInt(this.limit);
  }

  /**
   * Set page
   * @param page
   * @returns {CollectionFilter}
   */
  setPage(page) {
    this.page = parseInt(page);
    return this;
  }

  /**
   * @returns {number}
   */
  getPage() {
    return parseInt(this.page);
  }

  /**
   *
   * @param field
   * @param operator
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterAdd(field, operator, value, logic) {

    logic = (logic ? logic.toLowerCase().trim() : null);

    // normalize logic
    switch (logic) {
      case 'or':
      case 'whereor':
      case 'orwhere':
        logic = 'OrWhere';
        break;
      case 'and':
      case 'where':
      case 'whereand':
      default:
        logic = 'where';
        break;
    }

    // join array values with a pipe separator
    if (Array.isArray(value)) {
      value = value.join('|');
    }

    // init object
    let filter = {
      field: field,
      operator: operator,
      value: value,
      logic: logic
    };

    // add filter object ot filters
    this.filters.push(filter);

    // return chainable
    return this;
  }

  /**
   * String contains filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterContains(field, value, logic) {
    return this.filterAdd(field, 'ct', value, logic);
  }

  /**
   * String NOT contains filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterNotContains(field, value, logic) {
    return this.filterAdd(field, 'nct', value, logic);
  }

  /**
   * String starts with filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterStarts(field, value, logic) {
    return this.filterAdd(field, 'sw', value, logic);
  }

  /**
   * String ends with filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterEnds(field, value, logic) {
    return this.filterAdd(field, 'ew', value, logic);
  }

  /**
   * Equals filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterEquals(field, value, logic) {
    return this.filterAdd(field, 'eq', value, logic);
  }

  /**
   * Not equals filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterNotEquals(field, value, logic) {
    return this.filterAdd(field, 'ne', value, logic);
  }

  /**
   * Greater than filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterGreater(field, value, logic) {
    return this.filterAdd(field, 'gt', value, logic);
  }

  /**
   * Greater than or equal filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterGreaterEqual(field, value, logic) {
    return this.filterAdd(field, 'ge', value, logic);
  }

  /**
   * Lesser than filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterLess(field, value, logic) {
    return this.filterAdd(field, 'le', value, logic);
  }

  /**
   * Lesser than or equal filter
   * @param field
   * @param value
   * @param logic
   * @returns {CollectionFilter}
   */
  filterLessEqual(field, value, logic) {
    return this.filterAdd(field, 'le', value, logic);
  }

  /**
   * In filter
   * @param field
   * @param array
   * @param logic
   * @returns {CollectionFilter}
   */
  filterIn(field, array, logic) {
    return this.filterAdd(field, 'in', array, logic);
  }

  /**
   * Not in filter
   * @param field
   * @param array
   * @param logic
   * @returns {CollectionFilter}
   */
  filterNotIn(field, array, logic) {
    return this.filterAdd(field, 'nin', array, logic);
  }

  /**
   * Null filter
   * @param field
   * @param logic
   * @returns {CollectionFilter}
   */
  filterNull(field, logic) {
    return this.filterAdd(field, 'null', null, logic);
  }

  /**
   * Not null filter
   * @param field
   * @param logic
   * @returns {CollectionFilter}
   */
  filterNotNull(field, logic) {
    return this.filterAdd(field, 'nnull', null, logic);
  }

  /**
   * Has filter
   * @param connection
   * @param logic
   * @returns {CollectionFilter}
   */
  filterHas(connection, logic) {
    return this.filterAdd(connection, 'has', null, logic);
  }

  /**
   * Set default sort filters
   * @param field
   * @param order
   * @returns {CollectionFilter}
   */
  defaultSortAdd(field, order) {

    // format logic
    order = order.toUpperCase().trim();

    // set logic
    switch (order) {
      case 'ASC':
      case 'DESC':
        break;
      default:
        order = 'ASC';
        break;
    }

    // init object
    let filter = {
      field: field,
      order: order
    };

    // add filter object ot filters
    this.default_sort.push(filter);

    // return chainable
    return this;
  }

  /**
   *
   * @param field
   * @returns {CollectionFilter}
   */
  defaultSortAddAsc(field) {
    return this.defaultSortAdd(field, 'ASC');
  }

  /**
   *
   * @param field
   * @returns {CollectionFilter}
   */
  defaultSortAddDesc(field) {
    return this.defaultSortAdd(field, 'DESC');
  }

  /**
   * Add sort filters
   * @param field
   * @param order
   * @returns {CollectionFilter}
   */
  sortAdd(field, order) {

    // format logic
    order = order.toUpperCase().trim();

    // set logic
    switch (order) {
      case 'ASC':
      case 'DESC':
        break;
      default:
        order = 'ASC';
        break;
    }

    // init object
    let filter = {
      field: field,
      order: order
    };

    // add filter object ot filters
    this.sort.push(filter);

    // return chainable
    return this;
  }

  /**
   *
   * @param field
   * @returns {CollectionFilter}
   */
  sortAddAsc(field) {
    return this.sortAdd(field, 'ASC');
  }

  /**
   *
   * @param field
   * @returns {CollectionFilter}
   */
  sortAddDesc(field) {
    return this.sortAdd(field, 'DESC');
  }

  /**
   * Add eager loading of connected data
   * @param table
   */
  connectionAdd(table) {

    // check if the connection already exists

    if (!this.connectionExists(table)) {
      // add filter object ot filters
      this.connections.push({
        connection: table
      });
    }
  }

  connectionExists(table) {
    let result = false;
    this.connections.forEach((obj) => {
      if (obj.connection === table) {
        result = true;
      }
    });
    return result;
  }

  /**
   * Merges the static filters with the non-static filters in the object
   * @returns {Array}
   */
  mergeFilters() {

    if (!Array.isArray(this.static_filters) || !this.static_filters.length) {
      return this.filters;
    }
    return this.static_filters.concat(this.filters);
  }

  /**
   * @returns {Array}
   */
  mergeSort() {
    if (!Array.isArray(this.sort) || !this.sort.length) {
      return this.default_sort;
    }
    return this.sort;
  }


}
